import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {brandApi} from '../../../../api/brand/api';
import type {BrandResource, CreateBrandRequest, UpdateBrandRequest, UpdateLogoRequest} from '../../../../api/brand/api.d';
import {getErrorMessage} from '../../../utils/requests/error';
import {selectActiveAccountId} from '../../Login/userSlice';
import {setBrand as setDefaultBrandInSettings, fetchDefaultBrand} from '../ThemesAndStyles/brandSettingsSlice';

export interface ErrorMessage {
    messageKey: string;
    parameters: Record<string, any>;
}

export interface BrandsState {
    brands: BrandResource[];
    isBrandsFetching: boolean;
    brandsFetchingError: ErrorMessage | null;
    isBrandSaving: boolean;
    brandSavingError: ErrorMessage | null;
    isBrandDeleting: boolean;
    brandDeletingError: ErrorMessage | null;
}

const initialState: BrandsState = {
    brands: [],
    isBrandsFetching: false,
    brandsFetchingError: null,
    isBrandSaving: false,
    brandSavingError: null,
    isBrandDeleting: false,
    brandDeletingError: null
};

export const brandsSlice = createSlice({
    name: 'brands',
    initialState,
    reducers: {
        setBrands: (state, action: PayloadAction<BrandResource[]>) => {
            state.brands = action.payload;
        },
        setIsBrandsFetching: (state, action: PayloadAction<boolean>) => {
            state.isBrandsFetching = action.payload;
        },
        setBrandsFetchingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.brandsFetchingError = action.payload;
        },
        setIsBrandSaving: (state, action: PayloadAction<boolean>) => {
            state.isBrandSaving = action.payload;
        },
        setBrandSavingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.brandSavingError = action.payload;
        },
        setIsBrandDeleting: (state, action: PayloadAction<boolean>) => {
            state.isBrandDeleting = action.payload;
        },
        setBrandDeletingError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.brandDeletingError = action.payload;
        }
    }
});

export const {
    setBrands,
    setIsBrandsFetching,
    setBrandsFetchingError,
    setIsBrandSaving,
    setBrandSavingError,
    setIsBrandDeleting,
    setBrandDeletingError
} = brandsSlice.actions;

export const fetchBrands = () => async (dispatch: any, getState: any) => {
    const accountId = selectActiveAccountId(getState());
    if (!accountId) return false;

    dispatch(setIsBrandsFetching(true));
    dispatch(setBrandsFetchingError(null));

    try {
        const result = await brandApi.getBrands(accountId) as any;
        dispatch(setBrands(result.data || []));
        dispatch(setIsBrandsFetching(false));
        return true;
    } catch {
        dispatch(setIsBrandsFetching(false));
        return false;
    }
};

export const createBrand = (data: CreateBrandRequest) => async (dispatch: any, getState: any) => {
    const accountId = selectActiveAccountId(getState());
    if (!accountId) return false;

    dispatch(setIsBrandSaving(true));
    dispatch(setBrandSavingError(null));

    try {
        await brandApi.createBrand(accountId, data);
        dispatch(setIsBrandSaving(false));
        dispatch(fetchDefaultBrand(accountId));
        return true;
    } catch (err) {
        dispatch(setIsBrandSaving(false));
        dispatch(setBrandSavingError(getErrorMessage(err)));
        return false;
    }
};

export const updateBrand = (brandId: string, data: UpdateBrandRequest) => async (dispatch: any, getState: any) => {
    const accountId = selectActiveAccountId(getState());
    if (!accountId) return false;

    dispatch(setIsBrandSaving(true));
    dispatch(setBrandSavingError(null));

    try {
        await brandApi.updateBrand(accountId, brandId, data);
        dispatch(setIsBrandSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsBrandSaving(false));
        dispatch(setBrandSavingError(getErrorMessage(err)));
        return false;
    }
};

export const updateBrandLogo = (brandId: string, data: UpdateLogoRequest) => async (dispatch: any, getState: any) => {
    const accountId = selectActiveAccountId(getState());
    if (!accountId) return false;

    dispatch(setIsBrandSaving(true));
    dispatch(setBrandSavingError(null));

    try {
        await brandApi.updateLogo(accountId, brandId, data);
        dispatch(setIsBrandSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsBrandSaving(false));
        dispatch(setBrandSavingError(getErrorMessage(err)));
        return false;
    }
};

export const setDefaultBrand = (brandId: string) => async (dispatch: any, getState: any) => {
    const accountId = selectActiveAccountId(getState());
    if (!accountId) return false;

    dispatch(setIsBrandSaving(true));
    dispatch(setBrandSavingError(null));

    try {
        const result = await brandApi.setDefaultBrand(accountId, brandId) as any;
        dispatch(setDefaultBrandInSettings(result?.data || null));
        dispatch(setIsBrandSaving(false));
        return true;
    } catch (err) {
        dispatch(setIsBrandSaving(false));
        dispatch(setBrandSavingError(getErrorMessage(err)));
        return false;
    }
};

export const deleteBrand = (brandId: string) => async (dispatch: any, getState: any) => {
    const accountId = selectActiveAccountId(getState());
    if (!accountId) return false;

    dispatch(setIsBrandDeleting(true));
    dispatch(setBrandDeletingError(null));

    try {
        await brandApi.deleteBrand(accountId, brandId);
        dispatch(setIsBrandDeleting(false));
        dispatch(fetchDefaultBrand(accountId));
        return true;
    } catch (err) {
        dispatch(setIsBrandDeleting(false));
        dispatch(setBrandDeletingError(getErrorMessage(err)));
        return false;
    }
};

export const selectBrands = (state: any) => state.brands?.brands || [];
export const selectIsBrandsFetching = (state: any) => state.brands?.isBrandsFetching;
export const selectBrandsFetchingError = (state: any) => state.brands?.brandsFetchingError;
export const selectIsBrandSaving = (state: any) => state.brands?.isBrandSaving;
export const selectBrandSavingError = (state: any) => state.brands?.brandSavingError;
export const selectIsBrandDeleting = (state: any) => state.brands?.isBrandDeleting;
export const selectBrandDeletingError = (state: any) => state.brands?.brandDeletingError;

export default brandsSlice.reducer;
